import { HttpAgent } from "@ag-ui/client";
import type { BaseEvent, RunAgentInput } from "@ag-ui/core";
import { authDataIdentity, subscribeAuthDataIdentity } from "./cacheScope";
import { apiBaseUrl, pb } from "./pocketbase";
import { ApiError } from "./api";
import { chatEventHistory, parseChatStatus, type ChatStatus } from "./assistant";
import { frontendDisplayTools, parseDisplayCall, reconcileDisplayComponents, type DisplayCall, type RenderResult } from "./assistant-display";
import { parseComponent, type ChatComponent } from "./assistant-components";
import { chatAdmissionKnown, chatAdmissionRejected, freezeChatAdmission, frozenChatAdmission, pendingRenderResults, pendingSelectionState } from "./assistant-outbox";
import { emptySelectionState, parseSelectionState, type SelectionState } from "./assistant-selection";

export interface ChatRunView {
  provisional: string;
  provisionalId: string;
  status: ChatStatus | null;
  events: Record<string, unknown>[];
  calls: DisplayCall[];
  components: ChatComponent[];
  pendingToolCallIds: string[];
}

export function chatRunInput(conversation: string, message: string, runId: string, messageId: string, results: RenderResult[] = [], state: SelectionState = emptySelectionState()): RunAgentInput {
  if (results.length > 1) throw new TypeError("At most one pending rendering result");
  return { threadId: conversation, runId, protocolVersion: "1.0", messages: [...results, { id: messageId, role: "user", content: message }],
    state: parseSelectionState(state), tools: frontendDisplayTools, context: [], forwardedProps: { schema: "neoma.run/3" } };
}

/** SDK state is presentation only. Never serialize accumulated history/state. */
class NeomaAgent extends HttpAgent {
  constructor(url: string, private readonly profile: RunAgentInput, fetcher: (url: string, init: RequestInit) => Promise<Response>) {
    super({ url, threadId: profile.threadId, fetch: fetcher });
  }
  protected override requestInit(): RequestInit {
    return { method: "POST", headers: { "Content-Type": "application/json", Accept: "text/event-stream" }, body: JSON.stringify(this.profile) };
  }
}

function diagnostic(event: BaseEvent): Record<string, unknown> {
  // Allowlisted actual protocol fields; no SDK inputs, rawEvent or private data.
  const v = event as unknown as Record<string, unknown>;
  const result: Record<string, unknown> = { type: v.type };
  for (const key of ["threadId", "runId", "messageId", "name", "code", "toolCallId", "toolCallName", "parentMessageId"]) if (typeof v[key] === "string") result[key] = v[key];
  if (typeof v.delta === "string" && v.type === "TEXT_MESSAGE_CONTENT") result.delta = v.delta.slice(0, 1000);
  if (v.type === "CUSTOM" && v.name === "neoma.run-status/1") {
    const status = parseChatStatus(v.value);
    result.value = { state: status.state, code: status.code, execution_id: status.execution_id,
      user_message_id: status.user_message_id, assistant_message_id: status.assistant_message_id };
  }
  return result;
}

async function consume(recording: string, conversation: string, requestId: string, profile: RunAgentInput,
  restore: boolean, onUpdate?: (view: ChatRunView) => void, signal?: AbortSignal): Promise<ChatStatus> {
  const fence = authDataIdentity(), controller = new AbortController();
  const unsubscribe = subscribeAuthDataIdentity(() => controller.abort());
  const timeout = AbortSignal.timeout(40000);
  const combined = AbortSignal.any([controller.signal, timeout, ...(signal ? [signal] : [])]);
  const check = () => {
    if (combined.aborted || fence !== authDataIdentity()) throw new DOMException("Assistant scope changed", "AbortError");
  };
  const path = `/platform/mobile/v1/recordings/${encodeURIComponent(recording)}/ai/` +
    (restore ? `runs/${encodeURIComponent(requestId)}/events` : "runs");
  const url = new URL(path, `${apiBaseUrl}/`).toString();
  let observedToken = "";
  const agent = new NeomaAgent(url, profile, async (target, init) => {
    check();
    const token = pb.authStore.token;
    observedToken = token;
    const response = await fetch(target, { ...init, ...(restore ? { method: "GET", body: undefined } : {}),
      headers: { ...init.headers, Authorization: `Bearer ${token}` }, signal: combined });
    check();
    if (!response.ok) {
      if (response.status === 401 && token === pb.authStore.token) {
        pb.authStore.clear(); window.dispatchEvent(new Event("pb-auth-invalid"));
      }
      if (response.status === 401 && token !== pb.authStore.token) throw new DOMException("Assistant credentials changed", "AbortError");
      const data = await response.json() as { error?: { code?: string; resets_at?: string } };
      check();
      if (restore && response.status === 404) {
        // A missing original run is not proof of access revocation or proof
        // that a lost POST cannot still admit. Verify conversation access,
        // retain the frozen input/report, and allow only its exact retry.
        await chatEventHistory(recording, conversation, 0, combined);
        check();
        throw new ApiError("admission_unknown", 409);
      }
      if (!restore && [400, 409, 413, 426, 429].includes(response.status)) chatAdmissionRejected(recording, conversation, requestId);
      throw new ApiError(data.error?.code || "Assistant observation unavailable", response.status,
        data.error?.resets_at ? { resets_at: data.error.resets_at } : undefined);
    }
    const reader = response.body?.getReader();
    if (!reader) throw new TypeError("Missing assistant stream");
    let size = 0;
    return new Response(new ReadableStream<Uint8Array>({
      async pull(stream) {
        try {
          const next = await reader.read(); check();
          if (next.done) { stream.close(); return; }
          size += next.value.byteLength;
          if (size > 2 * 1024 * 1024) throw new TypeError("Assistant stream too large");
          stream.enqueue(next.value);
        } catch (error) { await reader.cancel().catch(() => undefined); stream.error(error); }
      },
      cancel: () => reader.cancel(),
    }), { status: response.status, headers: response.headers });
  });
  let view: ChatRunView = { provisional: "", provisionalId: "", status: null, events: [], calls: [], components: [], pendingToolCallIds: [] };
  let savedCalls: DisplayCall[] = [], savedComponents: ChatComponent[] = [];
  let terminalSuccess = false;
  const buffers = new Map<string, { name: string; parent: string; arguments: string; ended: boolean }>();
  let eventFailure: unknown;
  const stop = (error: unknown) => { eventFailure = error; controller.abort(); return { stopPropagation: true }; };
  try {
    try { await agent.runAgent({ runId: requestId, tools: [], context: [] }, {
      onMessagesSnapshotEvent({ event }) {
        try { check(); } catch (error) { return { ...stop(error), messages: [] }; }
        // HttpAgent's default reducer preserves client-only messages. This
        // profile makes server snapshots authoritative, including removal of
        // failed provisional text and the server's canonical message order.
        return { messages: event.messages, stopPropagation: true };
      },
      onEvent({ event }) {
        try {
        check();
        const v = event as unknown as Record<string, unknown>;
        view = { ...view, events: [...view.events.slice(-127), diagnostic(event)] };
        if (v.type === "TEXT_MESSAGE_START") view.provisionalId = String(v.messageId);
        if (v.type === "TEXT_MESSAGE_CONTENT") view.provisional += String(v.delta);
        if (v.type === "TOOL_CALL_START") {
          if (buffers.size >= 1 || typeof v.toolCallId !== "string" || typeof v.toolCallName !== "string" || typeof v.parentMessageId !== "string") throw new TypeError("Invalid display sequence");
          buffers.set(v.toolCallId, { name: v.toolCallName, parent: v.parentMessageId, arguments: "", ended: false });
        }
        if (v.type === "TOOL_CALL_ARGS" || v.type === "TOOL_CALL_END") {
          const buffer = buffers.get(String(v.toolCallId));
          if (!buffer || buffer.ended) throw new TypeError("Incomplete display sequence; reconcile original run");
          if (v.type === "TOOL_CALL_ARGS") {
            buffer.arguments += String(v.delta);
            if (new TextEncoder().encode(buffer.arguments).length > 65536) throw new TypeError("Display arguments too large");
          } else {
            parseDisplayCall({ id: String(v.toolCallId), type: "function", function: { name: buffer.name, arguments: buffer.arguments } }, buffer.parent, conversation);
            buffer.ended = true;
          }
        }
        if (v.type === "MESSAGES_SNAPSHOT") {
          if (!Array.isArray(v.messages) || v.messages.length > 50) throw new TypeError("Invalid saved transcript");
          savedCalls = v.messages.flatMap(message => {
            if (message.role !== "assistant" || !message.toolCalls) return [];
            if (!Array.isArray(message.toolCalls) || message.toolCalls.length > 1) throw new TypeError("Invalid saved calls");
            return message.toolCalls.map((call: unknown) => parseDisplayCall(call, message.id, conversation));
          });
          view.provisional = "";
        }
        if (v.type === "STATE_SNAPSHOT") {
          const snapshot = v.snapshot as { schema?: string; components?: unknown[] };
          if (snapshot?.schema !== "neoma.component-state/2" || !Array.isArray(snapshot.components) || snapshot.components.length > 20) throw new TypeError("Invalid saved display state");
          savedComponents = snapshot.components.map(parseComponent);
        }
        if (v.type === "CUSTOM" && v.name === "neoma.run-status/1") {
          const status = parseChatStatus(v.value);
          if (status.conversation_id !== conversation || status.client_request_id !== requestId) throw new TypeError("Run association mismatch");
          view.status = status;
          chatAdmissionKnown(recording, conversation, requestId, status.attribution.component_state);
        }
        if (v.type === "RUN_FINISHED") {
          const outcome = v.outcome as { type?: string; pendingToolCallIds?: unknown[] } | undefined;
          if (outcome && outcome.type !== "success") throw new TypeError("Unexpected run outcome");
          const pending = outcome?.pendingToolCallIds || [];
          if (!Array.isArray(pending) || pending.length > 1 || pending.some(id => typeof id !== "string" || !savedCalls.some(c => c.id === id)) ||
            [...buffers.values()].some(buffer => !buffer.ended)) throw new TypeError("Incomplete display; reconcile original run");
          for (const [id, buffer] of buffers) {
            const saved = savedCalls.find(call => call.id === id);
            if (!saved || saved.arguments !== buffer.arguments || saved.parentMessageId !== buffer.parent || saved.name !== buffer.name) throw new TypeError("Saved display differs from stream");
          }
          if (view.status?.state !== "completed") throw new TypeError("Missing durable generation success");
          view = { ...view, calls: savedCalls, components: reconcileDisplayComponents(savedComponents, savedCalls), pendingToolCallIds: pending as string[] };
          terminalSuccess = true;
        }
        if (v.type === "RUN_ERROR" && v.code === "access_lost") {
          // The stream authenticates with the token captured at subscription.
          // Revalidate using refreshed credentials before clearing this same
          // principal's selected conversation and pending request identity.
          if (observedToken !== pb.authStore.token) throw new DOMException("Assistant credentials refreshed; restore original request", "AbortError");
          throw new ApiError("resource_not_found", 404);
        }
        onUpdate?.({ ...view });
        } catch (error) {
          // SDK subscriber exceptions are logged and swallowed. Retain an
          // explicit error and terminate observation so access failures reach
          // the application and cannot silently leave protected state visible.
          return stop(error);
        }
      },
    }); } catch (error) {
      // RUN_ERROR may describe a durably failed/uncertain request. Its saved
      // status is usable, while transport/auth errors still require recovery.
      if (eventFailure) throw eventFailure;
      check();
      if (error instanceof ApiError || !view.status || view.status.state === "pending") throw error;
      if (view.status.state === "completed") throw new TypeError("Incomplete assistant stream; reconcile original run");
    }
    if (eventFailure) throw eventFailure;
    check();
    if (!view.status) throw new TypeError("Missing durable run status");
    if (view.status.state === "completed" && !terminalSuccess) throw new TypeError("Incomplete assistant stream; reconcile original run");
    return view.status;
  } finally {
    unsubscribe();
    controller.abort();
    agent.setMessages([]); agent.setState({});
  }
}

export function runChat(recording: string, conversation: string, message: string, requestId: string,
  onUpdate?: (view: ChatRunView) => void, signal?: AbortSignal) {
  const messageId = crypto.randomUUID().replaceAll("-", "").slice(0, 15);
  const existing = frozenChatAdmission(recording, conversation, requestId);
  if (existing && existing.messages.at(-1)?.content !== message) throw new TypeError("Run ID input conflict");
  const input = existing || chatRunInput(conversation, message, requestId, messageId, pendingRenderResults(recording, conversation), pendingSelectionState(recording, conversation));
  return consume(recording, conversation, requestId, freezeChatAdmission(recording, conversation, input), false, onUpdate, signal);
}

export function observeChat(recording: string, conversation: string, requestId: string,
  onUpdate?: (view: ChatRunView) => void, signal?: AbortSignal) {
  // This input is local SDK initialization only; restore sends an authenticated
  // GET without a body and cannot admit another provider execution.
  return consume(recording, conversation, requestId, chatRunInput(conversation, "restore", requestId, "000000000000000"), true, onUpdate, signal);
}

export function retryChatAdmission(recording: string, conversation: string, requestId: string,
  onUpdate?: (view: ChatRunView) => void, signal?: AbortSignal) {
  const input = frozenChatAdmission(recording, conversation, requestId);
  if (!input) throw new TypeError("Original admission input is unavailable; reconcile saved conversation state");
  return consume(recording, conversation, requestId, input, false, onUpdate, signal);
}
